import { z } from 'zod';
import { isTruthy, isTuple } from './typing.js';
// ========== schema generation helper methods ==========
/**
 * Given a string enum, `enumReference`, creates a `union` of `literal`s with the values of the enum.
 * Works with enums with two or more values.
 * Additionally works with enum-like objects.
 */
export function enumToZodLiteralUnion(enumReference) {
    const values = Object.values(enumReference);
    const minLength = 2;
    if (!isTuple(values, minLength)) {
        throw new Error('A Union Requires at Least Two Enum Values');
    }
    // * explicit as conversion to satisfy when `map` is called on `values` is still a tuple of the same size as validated above
    const literals = values.map((value) => z.literal(value.toString()));
    return z.union(literals);
}
/**
 * Converts ALL [falsy values](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) of `T` into `null` instead, and allows `null`.
 * For `string` values, it trims whitespace before checking for falsy values.
 */
export function nullify(schema) {
    return z
        .union([
        z.string().trim(), // always trim strings, so they'll be empty for falsy checks
        z.any(),
    ])
        .transform((data) => data || null) // explicit unknown type to broaden the `any` input
        .pipe(schema.nullable());
}
/**
 * Converts ALL [falsy values](https://developer.mozilla.org/en-US/docs/Glossary/Falsy) of `T` into `undefined` instead, making them optional.
 * For `string` values, it trims whitespace before checking for falsy values.
 */
export function optionalize(schema) {
    return z
        .union([
        z.string().trim(), // always trim strings, so they'll be empty for falsy checks
        z.any(),
    ])
        .transform((data) => data || undefined)
        .pipe(schema.optional());
}
// ========== predefined schemas ==========
const trimmedString = z.string().trim();
const nonEmptyString = trimmedString.min(1, { message: 'Must not be empty.' });
const csvList = trimmedString
    .transform((data) => 
// could be a single value or many, separated by a comma and optional whitespace
data
    .split(',')
    .map((value) => value.trim())
    .filter(isTruthy))
    .transform((data) => data.join(', ')); // rejoin to a single string with comma and space
const emailList = trimmedString
    .transform((data) => data
    .split(',')
    .map((value) => value.trim())
    .filter(isTruthy))
    .pipe(z.array(trimmedString.email())) // could be a single email or a list of emails, separated by a comma and optional whitespace
    .transform((data) => data.join(', ')); // rejoin to a single string with comma and space
//  Lifted from [Zod Utils](https://github.com/JacobWeisenburger/zod_utilz/blob/main/src/json.ts)
const literalSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const jsonSchema = z.lazy(() => z.union([literalSchema, z.array(jsonSchema), z.record(jsonSchema)]));
const stringToJSONSchema = z
    .string()
    .transform((str, ctx) => {
    try {
        return jsonSchema.parse(JSON.parse(str));
    }
    catch (e) {
        ctx.addIssue({ code: 'custom', message: 'Invalid JSON' });
        return z.NEVER;
    }
});
/**
 * Lifted from [Zod Utils](https://github.com/JacobWeisenburger/zod_utilz/blob/main/src/stringToJSON.ts).
 *
 * `stringToJSON` is a schema that validates JSON encoded as a string, then returns the parsed value.
 *
 * @example
 * const schema = stringToJSON()
 * schema.parse('true') // true
 * schema.parse('null') // null
 * schema.parse('["one", "two", "three"]') // ['one', 'two', 'three']
 * schema.parse('<html>not a JSON string</html>') // throws
 * schema.pipe(z.object({foo: z.literal('bar')})).parse('{"foo":"bar"}') // {foo: 'bar'}
 */
export const stringToJSON = () => stringToJSONSchema;
/**
 * Creates a schema for numeric values to ensure they can be stored in a decimal with specific `precision` and `scale`.
 *
 * @param precision The total number of digits in the number.
 * @param scale The number of digits to the right of the decimal point.
 * @param message A custom error message if the validation fails.
 *
 * @see https://www.prisma.io/docs/orm/reference/prisma-schema-reference#decimal
 * @see https://www.postgresql.org/docs/current/datatype-numeric.html#DATATYPE-NUMERIC-DECIMAL
 */
const decimal = (precision, scale, message) => z.number().refine((value) => {
    const valueString = value.toString();
    const [integerPart, decimalPart] = valueString.split('.');
    if (!integerPart)
        return false;
    if (!decimalPart)
        return precision >= integerPart.length;
    return (precision >= integerPart.length + decimalPart.length &&
        scale >= decimalPart.length);
}, {
    message: message ??
        `Decimal must have at most ${precision} digits and at most ${scale} decimal places, otherwise it may overflow or be rounded`,
});
const insuranceProhibitedRefine = (value) => !/(insure|insurance|insured|insuring)/i.test(value);
const insuranceString = (max = Infinity) => nonEmptyString.max(max).refine(insuranceProhibitedRefine, {
    message: 'Please avoid using any variation of words like "insure" or "insurance"',
});
export const maxPolicyEligibilityDays = 999_999;
/** Predefined, reusable schemas with user-friendly error messages. */
// ? possibly rename, or restructure to be more similar to zod; i.e. `string.nullified` or `string.email.nullified`
export const helperSchema = {
    trimmedString,
    nonEmptyString,
    nullifiedInsuranceString: (max) => nullify(insuranceString(max)),
    insuranceString: (max) => insuranceString(max),
    /**
     * Converts falsy and white-space only strings into `null` when parsed, while still accepting `null` values.
     * Validates for `string` or `null` values removing whitespace, and transforms empty strings to `null`.
     * Inadvertently this ensures that all `string` results are non-empty.
     */
    nullifiedString: nullify(trimmedString),
    nullifiedEmail: nullify(trimmedString.email({ message: 'Must be a valid email.' })),
    csv: csvList,
    nullifiedUrl: nullify(trimmedString.url({ message: 'Must be a valid URL.' })),
    nullifiedEmailList: nullify(emailList), // ? probably needs a nicer message
    emailList,
    decimal,
    nonNegativeNumber: z
        .number({
        required_error: 'Must provide a number.',
        invalid_type_error: 'Must be a number.',
    })
        .nonnegative({ message: 'Must be zero or a positive number.' }),
    policyDays: z
        .number({
        required_error: 'Must provide a number.',
        invalid_type_error: 'Must be a number.',
    })
        .min(0, { message: 'Must be zero or a positive number.' })
        .max(maxPolicyEligibilityDays, {
        message: 'Must be less than 1,000,000',
    }),
    optionalizedString: optionalize(trimmedString),
    optionalizedDatetime: optionalize(trimmedString.datetime()),
};
//# sourceMappingURL=zodExtensions.js.map